import { useMemo } from "react";
import { AlertTriangle, Plus, X } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { S, T, scheduleTypeLabel } from "@/lib/strings";
import {
  WEEKDAY_LABELS,
  absoluteTime,
  cn,
  describeSchedule,
  firesPerHour,
  nextFireTimes,
  parseHhMm,
} from "@/lib/utils";
import type { Schedule, ScheduleType } from "@/types/fleet";

const TYPES: ScheduleType[] = ["NONE", "INTERVAL", "DAILY", "WEEKLY"];

/** Above this many runs an hour across the fleet, say so before it is saved. */
const FANOUT_WARN_PER_HOUR = 60;

/**
 * The four schedule types, with the two things that are invisible until it is
 * too late made visible up front:
 *
 *   1. the next three fire times, computed locally — "每 15 分鐘" is abstract,
 *      "14:15 / 14:30 / 14:45" is not;
 *   2. the fan-out. A 15-minute interval reads as harmless until it is
 *      multiplied by 87 phones and turns into 348 runs an hour.
 */
export function ScheduleBuilder({
  value,
  onChange,
  /** Devices this task is (or would be) assigned to, for the fan-out figure. */
  deviceCount,
  disabled,
}: {
  value: Schedule;
  onChange: (s: Schedule) => void;
  deviceCount: number | null;
  disabled?: boolean;
}) {
  const fires = useMemo(() => nextFireTimes(value, 3), [value]);
  const perHourPerDevice = firesPerHour(value);
  const fleetPerHour = deviceCount ? Math.round(perHourPerDevice * deviceCount * 10) / 10 : null;
  const heavy = fleetPerHour !== null && fleetPerHour >= FANOUT_WARN_PER_HOUR;

  const set = (patch: Partial<Schedule>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-3">
      <Field label={S.sectionSchedule}>
        <Select
          value={value.type}
          disabled={disabled}
          onChange={(e) => {
            const type = e.target.value as ScheduleType;
            // Clear the fields the new type does not use, so a task saved as
            // WEEKLY does not carry a stale intervalMinutes the device ignores.
            set({
              type,
              intervalMinutes: type === "INTERVAL" ? (value.intervalMinutes ?? 60) : null,
              timesOfDay: type === "DAILY" || type === "WEEKLY" ? value.timesOfDay : [],
              daysOfWeek: type === "WEEKLY" ? value.daysOfWeek : [],
            });
          }}
          options={TYPES.map((t) => ({ value: t, label: scheduleTypeLabel(t) }))}
        />
      </Field>

      {value.type === "INTERVAL" && (
        <Field label={S.fieldInterval}>
          <Input
            type="number"
            min={1}
            disabled={disabled}
            value={value.intervalMinutes ?? ""}
            onChange={(e) =>
              set({ intervalMinutes: e.target.value === "" ? null : Number(e.target.value) })
            }
            className="max-w-[160px]"
          />
        </Field>
      )}

      {(value.type === "DAILY" || value.type === "WEEKLY") && (
        <Field label={S.fieldTimes}>
          <div className="space-y-1.5">
            {value.timesOfDay.map((t, i) => {
              const invalid = parseHhMm(t) === null;
              return (
                <div key={i} className="flex items-center gap-1.5">
                  <Input
                    value={t}
                    disabled={disabled}
                    placeholder="09:00"
                    onChange={(e) => {
                      const next = [...value.timesOfDay];
                      next[i] = e.target.value;
                      set({ timesOfDay: next });
                    }}
                    className={cn("max-w-[110px] font-mono", invalid && "border-[#ef4444]")}
                    aria-invalid={invalid}
                  />
                  {invalid && <span className="text-[11px] text-[#fca5a5]">{S.invalidTime}</span>}
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => set({ timesOfDay: value.timesOfDay.filter((_, j) => j !== i) })}
                    className="text-[var(--color-fg-subtle)] hover:text-[#fca5a5] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
                    aria-label={`${S.delete} ${t}`}
                  >
                    <X size={13} />
                  </button>
                </div>
              );
            })}
            <Button
              size="sm"
              variant="default"
              disabled={disabled}
              onClick={() => set({ timesOfDay: [...value.timesOfDay, "09:00"] })}
            >
              <Plus size={12} />
              {S.addTime}
            </Button>
          </div>
        </Field>
      )}

      {value.type === "WEEKLY" && (
        <Field label={S.fieldWeekdays}>
          <div className="flex flex-wrap gap-1">
            {WEEKDAY_LABELS.map((label, i) => {
              const iso = i + 1;
              const on = value.daysOfWeek.includes(iso);
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={disabled}
                  aria-pressed={on}
                  onClick={() =>
                    set({
                      daysOfWeek: on
                        ? value.daysOfWeek.filter((d) => d !== iso)
                        : [...value.daysOfWeek, iso].sort((a, b) => a - b),
                    })
                  }
                  className={cn(
                    "size-8 rounded-md border text-xs font-medium transition-colors",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]",
                    on
                      ? "border-[var(--color-accent-bg)] bg-[var(--color-accent-bg)] text-white"
                      : "border-[var(--color-border)] bg-[rgba(255,255,255,0.03)] text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]",
                    disabled && "opacity-50",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </Field>
      )}

      {/* ---- always visible: what this actually does ---- */}
      <div className="rounded-lg border border-[var(--color-border-subtle)] bg-[rgba(255,255,255,0.02)] p-3">
        <div className="text-[11px] font-medium uppercase tracking-wider text-[var(--color-fg-subtle)]">
          {S.nextThreeFires}
        </div>
        {fires.length === 0 ? (
          <p className="mt-1 text-xs text-[var(--color-fg-faint)]">{S.noFireTimes}</p>
        ) : (
          <ul className="mt-1 space-y-0.5 font-mono text-xs text-[var(--color-fg-muted)]">
            {fires.map((d) => (
              <li key={d.getTime()}>{absoluteTime(d)}</li>
            ))}
          </ul>
        )}
        <p className="mt-1.5 text-[11px] text-[var(--color-fg-faint)]">{describeSchedule(value)}</p>
      </div>

      {heavy && fleetPerHour !== null && deviceCount !== null && (
        <div className="flex items-start gap-2 rounded-lg border border-[rgba(245,158,11,0.25)] bg-[rgba(245,158,11,0.06)] p-3">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[#f59e0b]" />
          <div>
            <div className="text-xs font-medium text-[#fcd34d]">{S.fanoutWarning}</div>
            <p className="mt-0.5 font-mono text-[11px] text-[var(--color-fg-muted)]">
              {T.fanout(deviceCount, describeSchedule(value), fleetPerHour)}
            </p>
            <p className="mt-1 text-[11px] text-[var(--color-fg-subtle)]">
              每次執行都會佔用裝置及一次 LLM 呼叫。考慮拉長間隔，或只指派給部分裝置。
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-[var(--color-fg-subtle)]">
        {label}
      </div>
      {children}
    </div>
  );
}
